<template>
  <section class="payment-point">
    <div class="left">
      <div class="title">
        <span>{{ t('title.point_use') }}</span>
      </div>
      <div class="remaining-point">
        <div class="point-title">잔여 포인트</div>
        <div class="point-amount">{{ formatCurrency(customer.Point) }} <span>P</span></div>
      </div>
      <div class="customer-info">
        <div class="info-item">
          <div class="label">{{ t('label.mobile_number') }}</div>
          <div class="value">{{ maskedMobileNumber }}</div>
        </div>
        <div class="info-item">
          <div class="label">{{ t('label.remaining_amount') }}</div>
          <div class="value">
            <span class="remaining-amount">{{ formatCurrency(paymentStore.remainingAmount) }}</span>
          </div>
        </div>
      </div>
      <div class="hero-image"><img src="@/assets/images/img-point.png" alt="포인트 이미지" /></div>
    </div>
    <div class="right">
      <div class="display">
        <span class="title">{{ t('label.use_point') }}</span>
        <span class="input-amount">{{ formatCurrency(Number(point || 0)) }} <span>P</span></span>
      </div>
      <BaseKeypad v-model="point" :max-value="maxPoint" />
      <div class="button">
        <BaseButtonGroup>
          <BaseButton class="outline" :flex="1" @mouseup="handlePrevClick" @touchend.prevent="handlePrevClick">
            <template #icon>
              <svg xmlns="http://www.w3.org/2000/svg" width="29.442" height="25.247" viewBox="0 0 29.442 25.247">
                <path
                  id="arrow-left-solid"
                  d="M.641,75.063a2.106,2.106,0,0,0,0,2.977L11.157,88.556a2.105,2.105,0,0,0,2.977-2.977L7.2,78.652H27.364a2.1,2.1,0,1,0,0-4.206H7.207l6.921-6.927a2.105,2.105,0,1,0-2.977-2.977L.635,75.057Z"
                  transform="translate(-0.025 -63.925)"
                />
              </svg>
            </template>
            {{ t('button.prev') }}
          </BaseButton>
          <BaseButton :flex="2" @mouseup="handleConfirmClick" @touchend.prevent="handleConfirmClick">
            <template #icon>
              <img src="@/assets/images/icon-check-white.svg" alt="확인 아이콘" />
            </template>
            {{ t('button.confirm') }}
          </BaseButton>
        </BaseButtonGroup>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
  import { usePoints } from '@/api/server';
  import { usePayment } from '@/composable/usePayment';
  import { PaymentMethod } from '@/enums/PaymentMethod';
  import UserError from '@/error';
  import OrderCloudCustomer from '@/interfaces/OrderCloudCustomer';
  import RequestPoint from '@/interfaces/RequestPoint';
  import { useCommonStore } from '@/stores/common';
  import { useConfigStore } from '@/stores/config';
  import { useModalStore } from '@/stores/modal';
  import { useOrderStore } from '@/stores/order';
  import { usePaymentStore } from '@/stores/payment';
  import { formatCurrency } from '@/utils';
  import { computed, onMounted, ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import BaseButton from './base/BaseButton.vue';
  import BaseButtonGroup from './base/BaseButtonGroup.vue';
  import BaseKeypad from './base/BaseKeypad.vue';

  const props = defineProps<{
    customer: OrderCloudCustomer;
  }>();

  const { t } = useI18n();
  const commonStore = useCommonStore();
  const configStore = useConfigStore();
  const modalStore = useModalStore();
  const paymentStore = usePaymentStore();
  const orderStore = useOrderStore();

  const { createPaymentSplit } = usePayment();

  const maxPoint =
    props.customer.Point > paymentStore.remainingAmount ? paymentStore.remainingAmount : props.customer.Point;

  // 입력한 포인트
  const point = ref(maxPoint.toString());

  const maskedMobileNumber = computed(() => {
    return props.customer.Mobile.replace(/(\d{3})(\d{4})(\d{4})/, '$1-****-$3');
  });

  onMounted(() => {
    // 포인트가 0이거나 잔여금액이 0이면 포인트 사용 불가
    if (props.customer.Point <= 0 || paymentStore.remainingAmount <= 0) {
      commonStore.showDialog({
        type: 'alert',
        message: t('error.unavailable_point'),
      });
      modalStore.resolveSyncModal<number>(0);
      return;
    }
  });

  /** 이전 버튼 클릭 */
  const handlePrevClick = () => modalStore.resolveSyncModal<number>(0);

  /** 확인 버튼 클릭 */
  const handleConfirmClick = async () => {
    if (!point.value) throw new UserError(t('error.input_point'));

    // 적립된 포인트가 결제 가능한 최소 가용 포인트 보다 적으면 사용 불가
    if (props.customer.Point < configStore.config.PayableMinPoints)
      throw new UserError(t('error.min_point_use_amount', { amount: configStore.config.PayableMinPoints }));

    // 포인트 사용 처리
    const pointResult = await usePoints({
      CardNo: props.customer.CardNo,
      Amount: Number(point.value),
    } satisfies RequestPoint);

    // 포인트 결제 분할 데이터 생성
    const paymentSplit = createPaymentSplit(PaymentMethod.Point, Number(point.value), pointResult);

    // 분할 결제 데이터에 추가
    paymentStore.addPaymentSplit(paymentSplit);

    // 고객 정보에 변경된 포인트 반영
    orderStore.setCustomer({ ...props.customer, Point: pointResult.PointBalance });

    // 입력한 포인트 반환
    modalStore.resolveSyncModal<number>(Number(point.value));
    return;
  };
</script>

<style lang="scss" scoped>
  @use '@/assets/scss/variables' as vars;

  .payment-point {
    display: grid;
    grid-template-columns: 42.9688vw 1fr;
    width: 100%;
    height: 100%;
    @media (max-width: vars.$sm) {
      grid-template-columns: auto;
      grid-template-rows: auto 1fr;
    }

    .left {
      display: grid;
      grid-template-rows: auto auto auto 1fr;
      padding: 7.0313vw;
      background-color: #f3f4f7;
      @media (max-width: vars.$sm) {
        padding: 20px;
      }

      .title {
        span {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          height: 3.125vw;
          padding: 0 1.4925vw;
          background-color: #000000;
          color: #ffffff;
          border-radius: 1.5625vw;
          font-size: 1.4925vw;
          @media (max-width: vars.$sm) {
            font-size: 18px;
            height: 30px;
            border-radius: 15px;
          }
        }
      }

      .remaining-point {
        margin-top: 1.5625vw;
        word-break: keep-all;
        @media (max-width: vars.$sm) {
          margin-top: 20px;
        }

        .point-title {
          font-size: 3.125vw;
          font-weight: 500;
          color: #777777;
          @media (max-width: vars.$sm) {
            font-size: 22px;
          }
        }

        .point-amount {
          font-size: 3.9063vw;
          font-weight: 600;
          @media (max-width: vars.$sm) {
            font-size: 24px;
          }
          span {
            color: var(--brand-primary);
          }
        }
      }

      .customer-info {
        display: grid;
        gap: 1.1719vw;
        margin-top: 3.5938vw;
        font-size: 2.1875vw;
        @media (max-width: vars.$sm) {
          margin-top: 20px;
          font-size: 18px;
        }

        .info-item {
          display: grid;
          grid-template-columns: 11.7188vw 1fr;
          .label {
            border-right: 1px solid #000000;
          }
          .value {
            text-align: right;
            font-size: 2.3438vw;

            .remaining-amount {
              font-weight: 600;
              color: var(--point-color-secondary);
            }
          }
        }
      }

      .hero-image {
        display: grid;
        justify-self: center;
        align-items: center;
        height: 100%;
        text-align: center;
        @media (max-width: vars.$sm) {
          display: none;
        }
        img {
          width: 22.8125vw;
        }
      }
    }

    .right {
      display: grid;
      grid-template-rows: auto 1fr auto;
      gap: 3.9063vw;
      padding: 7.0313vw 6.25vw 1.5625vw;
      @media (max-width: vars.$sm) {
        gap: 20px;
        padding: 20px;
      }

      .display {
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-bottom: 1px solid #000000;
        @media (max-width: vars.$sm) {
          font-size: 30px;
        }

        .title {
          font-size: 2.3438vw;
          color: #777777;

          @media (max-width: vars.$sm) {
            font-size: 20px;
          }
        }

        .input-amount {
          font-size: 3.9063vw;
          font-weight: 600;

          @media (max-width: vars.$sm) {
            font-size: 24px;
          }
          span {
            color: var(--brand-primary);
          }
        }
      }
    }
  }
</style>
